// ============================================================================
// CASE GROUP SERVICE — Phase A
// ============================================================================

import { get, post, put, del } from '../client/axiosClient';
import { caseGroupEndpoints, CASE_GROUP_BASE_PATH } from '../endpoints/caseGroup.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type { ApiResponse, EmptyResponse, PaginatedResponse, ID } from '../types';
import type {
  AddMembersRequest,
  BulkSaveStageRequest,
  BulkSaveStageResult,
  CaseGroup,
  CaseGroupCandidate,
  CaseGroupCandidateSearchRequest,
  CaseGroupListItem,
  CaseGroupSearchRequest,
  CaseMembership,
  CreateCaseGroupRequest,
  PreSaleLetter,
  UpdateCaseGroupRequest,
} from '../types/caseGroup.types';

const toPaginated = <T>(raw: any): PaginatedResponse<T> => {
  const paged = raw?.data ?? {};
  return {
    success: raw?.success ?? false,
    message: raw?.message,
    data: paged.items ?? [],
    pagination: {
      currentPage: paged.page ?? 1,
      pageSize: paged.pageSize ?? 25,
      totalPages: paged.totalPages ?? 0,
      totalItems: paged.totalCount ?? 0,
      hasNextPage: paged.hasNext ?? false,
      hasPreviousPage: paged.hasPrevious ?? false,
    },
  };
};

class CaseGroupService {
  async getAll(params?: CaseGroupSearchRequest): Promise<PaginatedResponse<CaseGroupListItem>> {
    const endpoint = params
      ? buildEndpointWithQuery(CASE_GROUP_BASE_PATH, params as Record<string, any>)
      : caseGroupEndpoints.list();
    const res = await get<any>(endpoint);
    return toPaginated<CaseGroupListItem>(res.data);
  }

  async getById(id: ID): Promise<CaseGroup | null> {
    const res = await get<ApiResponse<CaseGroup>>(caseGroupEndpoints.getById(id));
    return res.data?.data ?? null;
  }

  async create(req: CreateCaseGroupRequest): Promise<CaseGroup> {
    const res = await post<ApiResponse<CaseGroup>>(caseGroupEndpoints.create(), req);
    return res.data.data!;
  }

  async update(id: ID, req: UpdateCaseGroupRequest): Promise<CaseGroup> {
    const res = await put<ApiResponse<CaseGroup>>(caseGroupEndpoints.update(id), req);
    return res.data.data!;
  }

  async delete(id: ID): Promise<EmptyResponse> {
    const res = await del<EmptyResponse>(caseGroupEndpoints.delete(id));
    return res.data;
  }

  async getCandidates(params?: CaseGroupCandidateSearchRequest): Promise<PaginatedResponse<CaseGroupCandidate>> {
    const endpoint = buildEndpointWithQuery(`${CASE_GROUP_BASE_PATH}/candidates`, (params ?? {}) as Record<string, any>);
    const res = await get<any>(endpoint);
    return toPaginated<CaseGroupCandidate>(res.data);
  }

  async addMembers(id: ID, req: AddMembersRequest): Promise<CaseGroup> {
    const res = await post<ApiResponse<CaseGroup>>(caseGroupEndpoints.addMembers(id), req);
    return res.data.data!;
  }

  async removeMember(memberId: ID): Promise<EmptyResponse> {
    const res = await del<EmptyResponse>(caseGroupEndpoints.removeMember(memberId));
    return res.data;
  }

  async bulkSaveStage(id: ID, req: BulkSaveStageRequest): Promise<BulkSaveStageResult> {
    const res = await post<ApiResponse<BulkSaveStageResult>>(caseGroupEndpoints.bulkSaveStage(id), req);
    return res.data.data!;
  }

  async getByCase(caseId: ID): Promise<CaseMembership[]> {
    const res = await get<ApiResponse<CaseMembership[]>>(caseGroupEndpoints.byCase(caseId));
    return res.data?.data ?? [];
  }

  async getPreSaleLetters(id: ID): Promise<PreSaleLetter[]> {
    const res = await get<ApiResponse<PreSaleLetter[]>>(caseGroupEndpoints.preSaleLetters(id));
    return res.data?.data ?? [];
  }
}

export const caseGroupService = new CaseGroupService();
export default caseGroupService;
